import { Avatar, Box, Button, Skeleton, Typography } from '@mui/material'
import { useState } from 'react'
import { Divider, Switch } from 'utoniq-ui'

import { Artist, ArtistFavoriteMember } from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { DigitalMembershipCardPreview } from '@/components/organisms/digitalMembershipCard'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

import { DisableMembershipCardModal } from './DisableMembershipCardModal'
import { EnableMembershipCardWarningModal } from './EnableMembershipCardWarningModal'

export type State = {
  user: any | null
  artistId: string
  loading: boolean
  error?: any
  artist: Pick<Artist, 'id' | 'displayName' | 'digitalMembershipCard'> | null
  favoriteMembers: Pick<
    ArtistFavoriteMember,
    'id' | 'name' | 'avatar' | 'orderNumber' | 'digitalMembershipCard'
  >[]
  enableDigitalMembershipCard: boolean
  changingSettings: boolean
}

export type Action = {
  onEnableSettings: () => Promise<void>
  onDisableSettings: () => Promise<void>
}

export const ConsoleDigitalMembershipCardTemplate: Template<State, Action> = ({
  state,
  onEnableSettings,
  onDisableSettings,
}) => {
  const { routerPages, router, generatePath } = useRouter()
  const { t } = useTranslation('consoleDigitalMembershipCard')
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [showEnableWarningModal, setShowEnableWarningModal] = useState(false)

  const handleToggleSettings = async () => {
    if (state.enableDigitalMembershipCard) {
      // 推しメンバー機能を無効化する場合は確認モーダルを表示
      setShowDisableModal(true)
      return
    }

    if (!state.artist?.digitalMembershipCard?.cardDesignPath) {
      // 基本の会員証デザインが設定されていない場合は有効化させない
      setShowEnableWarningModal(true)
      return
    }
    await onEnableSettings()
  }

  const handleDisableSettings = async () => {
    await onDisableSettings()
    setShowDisableModal(false)
  }

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleMembership,
          title: t('title'),
        }}
      >
        <Box sx={{ px: 5, py: 5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1, pb: 3 }}>
              {state.loading ? (
                <>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={32}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="80%" height={20} />
                </>
              ) : (
                <>
                  <Typography variant="h5">{t('settingsTitle')}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      pt: 1,
                      display: 'block',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 300,
                      lineHeight: '20px',
                    }}
                  >
                    {t('description')}
                  </Typography>
                </>
              )}
            </Box>

            <Box sx={{ ml: 2, pt: 0.5 }}>
              {state.loading && !state.changingSettings ? (
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={24}
                  sx={{ borderRadius: 12 }}
                />
              ) : (
                <Switch
                  checked={state.enableDigitalMembershipCard}
                  onChange={handleToggleSettings}
                  color="primary"
                  disabled={state.changingSettings}
                />
              )}
            </Box>
          </Box>
          <Divider sx={{ borderWidth: 4, borderColor: 'grey.50', mb: 4 }} />

          <Box sx={{ mb: 8 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('defaultCardTitle')}
            </Typography>
            <DigitalMembershipCardSection
              digitalMembershipCard={{
                cardDesignPath:
                  state.artist?.digitalMembershipCard?.cardDesignPath,
                textColor: state.artist?.digitalMembershipCard?.textColor,
                displayAreaColor:
                  state.artist?.digitalMembershipCard?.displayAreaColor ??
                  undefined,
              }}
              loading={state.loading}
              onChangeDesign={() =>
                router.push(
                  routerPages.ConsoleDigitalMembershipDefaultCardDesignEdit
                )
              }
            />
          </Box>
          <Box sx={{ mb: 8 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('favoriteMemberCardsTitle')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '20px',
              }}
            >
              {t('favoriteMemberCardsDescription')}
            </Typography>
            {state.favoriteMembers.length > 0 ? (
              <>
                {state.favoriteMembers.map(favoriteMember => (
                  <DigitalMembershipCardSection
                    key={favoriteMember.id}
                    favoriteMember={favoriteMember}
                    digitalMembershipCard={{
                      cardDesignPath:
                        favoriteMember.digitalMembershipCard?.cardDesignPath,
                      textColor:
                        favoriteMember.digitalMembershipCard?.textColor,
                      displayAreaColor:
                        favoriteMember.digitalMembershipCard
                          ?.displayAreaColor ?? undefined,
                    }}
                    loading={state.loading}
                    onChangeDesign={() =>
                      router.push(
                        generatePath(
                          routerPages.ConsoleDigitalMembershipCardDesignEdit,
                          {
                            favoriteMemberId: favoriteMember.id,
                          }
                        )
                      )
                    }
                  />
                ))}
              </>
            ) : (
              <Box
                sx={{
                  backgroundColor: '#F7F9FA',
                  borderRadius: '8px',
                  px: '20px',
                  py: '12px',
                  mb: 3,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    fontSize: '13px',
                    fontStyle: 'normal',
                    fontWeight: 300,
                    lineHeight: '20px',
                  }}
                >
                  {t('noFavoriteMember')}
                </Typography>
                <Typography
                  component="a"
                  onClick={e => {
                    router.push(routerPages.ConsoleFavoriteMemberSettings)
                  }}
                  sx={{
                    color: 'primary.main',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    '&::after': {
                      content: '" >"',
                      marginLeft: '4px',
                    },
                  }}
                >
                  {t('confirmFavoriteMember')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </ConsoleBaseLayout>

      {/* デジタル会員証機能無効化確認モーダル */}
      <DisableMembershipCardModal
        open={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onDisable={handleDisableSettings}
      />
      {/* デジタル会員証機能有効化警告モーダル */}
      <EnableMembershipCardWarningModal
        open={showEnableWarningModal}
        onClose={() => setShowEnableWarningModal(false)}
      />
    </ArtistThemeProvider>
  )
}

interface DigitalMembershipCardSectionProps {
  favoriteMember?: Pick<ArtistFavoriteMember, 'id' | 'name' | 'avatar'>
  digitalMembershipCard: {
    cardDesignPath?: string
    textColor?: string
    displayAreaColor?: string
  }
  loading?: boolean
  onChangeDesign: () => void
}

const DigitalMembershipCardSection: React.FC<DigitalMembershipCardSectionProps> =
  ({
    favoriteMember,
    digitalMembershipCard,
    loading = false,
    onChangeDesign,
  }) => {
    const { t } = useTranslation('consoleDigitalMembershipCard')

    return (
      <Box sx={{ mb: 8 }}>
        {favoriteMember && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            {favoriteMember?.avatar?.smallUri && (
              <Avatar
                src={favoriteMember.avatar.smallUri}
                alt={favoriteMember.name}
                sx={{ width: 40, height: 40 }}
              />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {favoriteMember?.name}
            </Typography>
          </Box>
        )}

        <DigitalMembershipCardPreview
          digitalMembershipCard={digitalMembershipCard}
          loading={loading}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onChangeDesign}
            disabled={loading}
            sx={{
              borderColor: '#E0E0E0',
              color: '#666',
              '&:hover': {
                borderColor: '#BDBDBD',
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            {t('changeDesign')}
          </Button>
        </Box>
      </Box>
    )
  }
